import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useToast } from "../hooks/use-toast";

const SignUpPage = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    interests: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = "https://considerate-benevolence-production-2e2a.up.railway.app";

      console.log("Submitting to:", `${API_URL}/api/notify-signup`);
      console.log("Form data:", formData);
      
      const response = await fetch(`${API_URL}/api/notify-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
        }),
      });
      
      console.log("Response status:", response.status);
      console.log("Response ok:", response.ok);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log("Response data:", result);
      
      toast({
        title: "Welcome to takeatrip NYC!",
        description: "You've successfully joined our community. We'll be in touch soon with upcoming adventures!",
        duration: 5000,
      });

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        interests: ""
      });
    } catch (error) {
      console.error("Signup error:", error);
      toast({
        title: "Oops! Something went wrong",
        description: `Please try again in a moment. Error: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };



  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(to bottom right, white, rgb(248 250 252), rgba(29, 71, 106, 0.05))'}}>
      <Navigation />
      
      {/* Hero Section */}
      <div className="pt-20 pb-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-slate-800 mb-6">
              Join Our Community
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Ready to embark on amazing adventures and build lasting connections? Sign up today and become part of the takeatrip NYC family.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            {/* Sign Up Form */}
            <Card className="border-blue-200/50 bg-white/90 backdrop-blur-sm shadow-xl">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl font-bold text-slate-800">
                  Get Started Today
                </CardTitle>
                <p className="text-slate-600">Fill out the form below to join our community</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-slate-700 font-medium">First Name</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        type="text"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="transition-colors duration-300"
                        style={{
                          borderColor: '#1d476a20',
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#1d476a'}
                        onBlur={(e) => e.target.style.borderColor = '#1d476a20'}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-slate-700 font-medium">Last Name</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        type="text"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="transition-colors duration-300"
                        style={{
                          borderColor: '#1d476a20',
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#1d476a'}
                        onBlur={(e) => e.target.style.borderColor = '#1d476a20'}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="transition-colors duration-300"
                      style={{
                        borderColor: '#1d476a20',
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#1d476a'}
                      onBlur={(e) => e.target.style.borderColor = '#1d476a20'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="interests" className="text-slate-700 font-medium">
                      Tell us about your interests (optional)
                    </Label>
                    <Textarea
                      id="interests"
                      name="interests"
                      value={formData.interests}
                      onChange={handleInputChange}
                      placeholder="What activities or destinations interest you most?"
                      className="transition-colors duration-300 min-h-[100px]"
                      style={{
                        borderColor: '#1d476a20',
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#1d476a'}
                      onBlur={(e) => e.target.style.borderColor = '#1d476a20'}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full text-white py-4 text-lg transition-all duration-300 transform hover:scale-105 shadow-lg group"
                    style={{
                      background: 'linear-gradient(to right, #1d476a, rgb(51 65 85))',
                      border: 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) e.target.style.background = 'linear-gradient(to right, #163d56, rgb(45 55 72))';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting) e.target.style.background = 'linear-gradient(to right, #1d476a, rgb(51 65 85))';
                    }}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Joining Community...
                      </span>
                    ) : (
                      "Join Our Community"
                    )}
                  </Button>
                </form>

                <div className="mt-8 pt-6 border-t border-blue-100/50">
                  <p className="text-center text-slate-600">
                    Have questions?{" "}
                    <Link 
                      to="/faq"
                      className="font-medium"
                      style={{color: '#1d476a'}}
                      onMouseEnter={(e) => e.target.style.color = '#163d56'}
                      onMouseLeave={(e) => e.target.style.color = '#1d476a'}
                    >
                      Check out our FAQ
                    </Link>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SignUpPage;